import React, { useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Toaster, toast } from 'react-hot-toast';

// Supabase initialization
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const supabase = createClient(supabaseUrl, supabaseKey);

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Types
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
interface ClientInfo {
  name: string;
  email: string;
  contact: string;
  service: string;
}

type FormStep = 'name' | 'email' | 'contact' | 'service' | 'success';



// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Step Counter
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const StepCounter: React.FC<{ current: number; total: number }> = ({ current, total }) => (
  <motion.div
    initial={{ opacity: 0, y: -16 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex items-center gap-3 text-white/60 text-base mb-6"
  >
    <span className="text-xl font-light text-white">{current}</span>
    <span className="text-white/30">/</span>
    <span className="text-base">{total}</span>
  </motion.div>
);

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Enhanced Input Field
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const EnhancedInput: React.FC<{
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  autoFocus?: boolean;
  onEnter?: () => void;
}> = ({ value, onChange, placeholder, type = "text", autoFocus, onEnter }) => {
  const [focused, setFocused] = useState(false);

  return (
    <div className="relative w-full max-w-2xl mx-auto group">
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={(e) => e.key === 'Enter' && onEnter && (e.preventDefault(), onEnter())}
        className="w-full py-5 px-0 bg-transparent border-none border-b-2 border-white/25 text-white text-2xl md:text-3xl lg:text-4xl focus:outline-none focus:border-white transition-all duration-400 placeholder:text-white/40 font-light tracking-tight"
      />
      <motion.div
        className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[#FF4E45] to-[#7148FC]"
        initial={{ width: '0%' }}
        animate={{ width: focused || value ? '100%' : '0%' }}
        transition={{ duration: 0.4 }}
      />
      {focused && (
        <motion.div
          className="absolute -bottom-4 left-0 right-0 h-12 bg-gradient-to-t from-[#FF4E45]/8 to-transparent blur-lg pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
          transition={{ duration: 0.4 }}
        />
      )}
    </div>
  );
};

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Service Card
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const ServiceCard: React.FC<{
  service: string;
  isSelected: boolean;
  onClick: () => void;
}> = ({ service, isSelected, onClick }) => (
  <motion.button
    type="button"
    onClick={onClick}
    whileHover={{ scale: 1.02, y: -3 }}
    whileTap={{ scale: 0.98 }}
    className={`relative p-6 rounded-2xl border text-left text-lg md:text-xl font-light transition-all duration-300
      ${isSelected
        ? 'border-[#FF4E45] bg-gradient-to-br from-[#FF4E45]/8 to-[#7148FC]/5 shadow-lg shadow-[#FF4E45]/10'
        : 'border-white/15 bg-white/[0.02] hover:border-white/30 hover:bg-white/[0.04]'
      }`}
  >
    <span className="relative z-10 flex items-center justify-between">
      {service}
      {isSelected && <CheckCircle className="h-6 w-6 text-[#FF4E45]" />}
    </span>
  </motion.button>
);

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Progress Bar Component
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const ProgressBar: React.FC<{ currentStep: FormStep }> = ({ currentStep }) => {
  const steps: FormStep[] = ['name', 'email', 'contact', 'service'];
  let progress = 0;

  if (currentStep === 'success') {
    progress = 100;
  } else if (steps.includes(currentStep)) {
    const index = steps.indexOf(currentStep);
    progress = ((index + 1) / steps.length) * 100;
  }

  return (
    <motion.div
      className="h-1 bg-gradient-to-r from-[#FF4E45] via-[#7148FC] to-[#FF4E45] shadow-md shadow-[#FF4E45]/20"
      initial={{ width: '0%' }}
      animate={{ width: `${progress}%` }}
      transition={{ duration: 0.7, ease: 'easeOut' }}
    />
  );
};

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Main ClientPortal Component
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const ClientPortal: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<FormStep>('name');
  const [clientInfo, setClientInfo] = useState<ClientInfo>({
    name: '',
    email: '',
    contact: '',
    service: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const serviceOptions = [
    'Web Development',
    'Mobile App Development',
    'UI/UX Design',
    'Digital Marketing',
    'SEO Optimization',
    'Content Creation',
    'Other',
  ];

  const updateField = (field: keyof ClientInfo, value: string) => {
    setClientInfo(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const checkIfEmailExists = async (email: string) => {
    const { data } = await supabase.from('clients').select('email').eq('email', email).single();
    return !!data;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.from('clients').insert([clientInfo]);
      if (error) throw error;
      setCurrentStep('success');
      toast.success('Thank you! We received your information.', { duration: 5000 });
    } catch (err: any) {
      const msg = err.message?.includes('duplicate')
        ? 'This email is already registered.'
        : 'Something went wrong. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinue = async () => {
    setError('');

    if (currentStep === 'name' && !clientInfo.name.trim()) return setError("Name is required");
    if (currentStep === 'email') {
      if (!clientInfo.email.trim()) return setError("Email is required");
      if (!validateEmail(clientInfo.email)) return setError("Invalid email address");
      try {
        if (await checkIfEmailExists(clientInfo.email)) {
          return setError("This email is already registered");
        }
      } catch { }
    }
    if (currentStep === 'contact' && !clientInfo.contact.trim()) return setError("Contact is required");
    if (currentStep === 'service' && !clientInfo.service.trim()) return setError("Please select a service");

    if (currentStep === 'service') {
      handleSubmit();
    } else {
      const nextStep: FormStep =
        currentStep === 'name' ? 'email' :
          currentStep === 'email' ? 'contact' :
            'service';
      setCurrentStep(nextStep);
    }
  };

  const titles: Record<FormStep, string> = {
    name: "What is your name?",
    email: "What's your email address?",
    contact: "What's your contact number?",
    service: "What service are you interested in?",
    success: "",
  };

  const placeholders: Record<FormStep, string> = {
    name: "John Doe",
    email: "john@company.com",
    contact: "+91 98765 43210",
    service: "Describe your project...",
    success: "",
  };

  const stepIndex = ['name', 'email', 'contact', 'service'].indexOf(currentStep);

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Background orbs */}
      <div className="fixed inset-0 pointer-events-none">
        <motion.div
          className="absolute top-1/3 -left-32 w-64 h-64 bg-[#FF4E45]/15 rounded-full blur-3xl"
          animate={{ x: [0, 80, 0], y: [0, 40, 0], scale: [1, 1.08, 1] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-1/3 -right-32 w-64 h-64 bg-[#7148FC]/10 rounded-full blur-3xl"
          animate={{ x: [0, -80, 0], y: [0, -40, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {/* Hero / Welcome Section */}
      <div className="min-h-screen flex flex-col items-center justify-center px-6 md:px-12 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-5xl"
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter leading-tight mb-8">
            We Onboard Only
            <br />
            <span className="bg-gradient-to-r from-[#FF4E45] to-[#7148FC] bg-clip-text text-transparent">2 Projects</span> a Month
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-white/70 max-w-3xl mx-auto leading-relaxed font-light">
            To give each brand the attention it deserves, we limit our workload.
            This ensures high-quality output, focused creativity, and a seamless experience.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
        >
          <div className="w-7 h-11 border border-white/25 rounded-full flex items-center justify-center p-1.5">
            <motion.div
              className="w-1.5 h-2.5 bg-gradient-to-b from-[#FF4E45] to-[#7148FC] rounded-full"
              animate={{ y: [0, 14, 0] }}
              transition={{ duration: 1.8, repeat: Infinity }}
            />
          </div>
          <span className="text-white/50 text-xs md:text-sm tracking-wider uppercase">Scroll down</span>
        </motion.div>
      </div>

      {/* Form Section */}
      <div className="px-6 md:px-12 lg:px-24 pb-24 pt-12 md:pt-20 relative z-10">
        {/* Progress bar - shown only in form section */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto mb-12"
        >
          <ProgressBar currentStep={currentStep} />
        </motion.div>
        <AnimatePresence mode="wait">
          {currentStep !== 'success' ? (
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.5 }}
              className="max-w-4xl mx-auto"
            >
              <div className="flex justify-center mb-10">
                <StepCounter current={stepIndex + 1} total={4} />
              </div>

              <motion.h2
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.15 }}
                className="text-2xl md:text-4xl lg:text-5xl font-light mb-12 md:mb-16 tracking-tight text-center"
              >
                {titles[currentStep]} <span className="text-[#FF4E45]">*</span>
              </motion.h2>

              {currentStep !== 'service' ? (
                <EnhancedInput
                  value={clientInfo[currentStep] || ''}
                  onChange={(e) => updateField(currentStep, e.target.value)}
                  placeholder={placeholders[currentStep]}
                  type={currentStep === 'email' ? 'email' : currentStep === 'contact' ? 'tel' : 'text'}
                  autoFocus
                  onEnter={handleContinue}
                />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                  {serviceOptions.map(opt => (
                    <ServiceCard
                      key={opt}
                      service={opt}
                      isSelected={clientInfo.service === opt}
                      onClick={() => {
                        updateField('service', opt);
                        if (opt !== 'Other') setTimeout(handleContinue, 280);
                      }}
                    />
                  ))}

                  {clientInfo.service === 'Other' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="col-span-full mt-4"
                    >
                      <EnhancedInput
                        value=""
                        onChange={(e) => updateField('service', e.target.value)}
                        placeholder="Please describe your project..."
                        onEnter={handleContinue}
                        autoFocus
                      />
                    </motion.div>
                  )}
                </div>
              )}

              {error && (
                <motion.p
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-8 text-red-400 text-center text-lg md:text-xl"
                >
                  {error}
                </motion.p>
              )}

              <div className="mt-12 md:mt-16 flex flex-col items-center gap-5">
                <motion.button
                  onClick={handleContinue}
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  className="relative px-10 py-4 rounded-full bg-gradient-to-r from-[#FF4E45] to-[#7148FC] text-white text-lg md:text-xl font-medium disabled:opacity-60 group overflow-hidden"
                >
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent"
                    animate={{ x: ['-150%', '150%'] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                  <span className="relative flex items-center gap-2.5">
                    {currentStep === 'service'
                      ? (isSubmitting ? 'Submitting...' : 'Submit Application')
                      : 'Continue'}
                    {!isSubmitting && <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />}
                  </span>
                </motion.button>

                <p className="text-white/50 text-sm md:text-base">
                  Press <kbd className="px-2.5 py-1 rounded bg-white/10 border border-white/20 text-white/70 font-mono text-xs">Enter â†µ</kbd> to continue
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="min-h-[70vh] flex items-center justify-center text-center px-6"
            >
              <div className="max-w-3xl">
                <motion.div
                  initial={{ scale: 0.6 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 180 }}
                  className="mb-10"
                >
                  <CheckCircle className="h-24 w-24 md:h-32 md:w-32 text-[#FF4E45] mx-auto" strokeWidth={1.4} />
                </motion.div>

                <h2 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6">
                  Thank You{clientInfo.name ? `, ${clientInfo.name.split(' ')[0]}` : ''}!
                </h2>

                <p className="text-xl md:text-2xl text-white/80 mb-8">
                  We've received your information.
                </p>

                <div className="inline-block px-7 py-4 rounded-2xl bg-gradient-to-br from-[#FF4E45]/8 to-[#7148FC]/5 border border-[#FF4E45]/15">
                  <p className="text-lg md:text-xl text-white/80">
                    Our team will reach out within <span className="text-[#FF4E45] font-medium">24â€“48 hours</span>
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <Toaster position="top-center" />
    </div>
  );
};

export default ClientPortal;
